import { z } from 'zod';
import { allItemsResponse, optionalCuid, decimalString } from './common';

/**
 * Schemas for the per-project Countertop line items.
 *
 * Path: `/projects/:projectId/countertops`
 *
 * Snapshot semantics:
 * - When created `from-catalog`, the line item copies name/sku/dimensions/
 *   material/color from the catalog item and sets `unitPriceNet`/`currency`
 *   from the request body (price is workshop-set, not catalog-cost).
 * - `catalogItemId` is recorded for traceability and is **immutable** once set.
 * - Subsequent edits to the catalog item do NOT propagate to existing lines.
 *
 * Worker access:
 * - Workers may LIST/GET countertops only on projects they are assigned to.
 * - Workers see no `unitPriceNet`/`currency` (financials hidden).
 * - Workers may not mutate (`PROJECT_ITEMS_CREATE/UPDATE/DELETE`).
 *
 * Status locking:
 * - Mutations are forbidden when the parent project is in a terminal status
 *   (`DELIVERED` or `REJECTED`).
 */

const positiveDecimal = decimalString.refine((v) => parseFloat(v) > 0, {
  message: 'Must be greater than 0',
});

const positiveIntegerDecimal = decimalString.refine(
  (v) => Number.isInteger(Number(v)) && Number(v) > 0,
  {
    message: 'Must be a positive integer',
  },
);

const nonNegativeDecimal = decimalString.refine((v) => parseFloat(v) >= 0, {
  message: 'Must be ≥ 0',
});

const dimMm = z.number().int().min(0).max(100_000);
const optionalDimMm = z.number().int().min(0).max(100_000).optional();

const countertopCurrencyEnum = z.enum(['RON', 'EUR', 'USD']);
export type CountertopCurrencyValue = z.infer<typeof countertopCurrencyEnum>;

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

export const countertopListItem = z.object({
  id: z.string(),
  projectId: z.string(),
  roomId: z.string().cuid().nullable(),
  areaId: z.string().cuid().nullable(),
  catalogItemId: z.string().nullable(),
  name: z.string(),
  sku: z.string().nullable(),
  widthMm: z.number().int(),
  depthMm: z.number().int(),
  thicknessMm: z.number().int(),
  material: z.string().nullable(),
  /**
   * The board(s) as the item tables show them, by the shared rule the quote PDF
   * uses (`formatMaterials`): the saved label when there is one, else the
   * catalog name. Filled by the list endpoints, which load the names; absent
   * on single-item responses. '' when the item carries no material.
   */
  materialDisplay: z.string().optional(),
  edgeProfile: z.string().nullable(),
  doubled: z.boolean(),
  quantity: decimalString,
  coefficient: decimalString,
  materialCoefficient: decimalString,
  unitPriceNet: decimalString.optional(),
  /**
   * Unit price WITHOUT the markup: MANUAL rows repeat `unitPriceNet`; AUTO rows
   * report the engine's catalog-cost sum (`subtotalBaseNet`), absent until a
   * breakdown stored before that field existed is recomputed. Absent when
   * finances are hidden.
   */
  unitPriceBaseNet: decimalString.optional(),
  /**
   * Unit price WITH the markup: `unitPriceNet` times the row coefficient. On
   * AUTO rows the markups already live inside `unitPriceNet`, so the two agree
   * there by construction. Absent when finances are hidden.
   */
  unitPriceFinalNet: decimalString.optional(),
  currency: countertopCurrencyEnum.optional(),
  totalNet: decimalString.optional(),
  pricingMode: z.enum(['MANUAL', 'AUTO']),
  materialId: z.string().nullable(),
  edgeId: z.string().nullable(),
  computedBreakdown: z.unknown().nullable().optional(),
  pricingError: z.string().optional(),
  orderIndex: z.number().int(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type CountertopListItem = z.infer<typeof countertopListItem>;

export const countertopDetails = countertopListItem;
export type CountertopDetails = z.infer<typeof countertopDetails>;

export const listCountertopsResponse = allItemsResponse(countertopListItem);
export type ListCountertopsResponse = z.infer<typeof listCountertopsResponse>;

// ----- Manual create (no catalog reference) -----
export const createCountertopRequest = z.object({
  roomId: optionalCuid(),
  areaId: optionalCuid(),
  name: z.string().trim().min(1).max(200),
  sku: optionalTrim(50),
  widthMm: dimMm,
  depthMm: dimMm,
  thicknessMm: dimMm,
  material: optionalTrim(100),
  edgeProfile: optionalTrim(100),
  quantity: positiveIntegerDecimal.default('1'),
  coefficient: positiveDecimal.default('2.5'),
  /**
   * Markup for THIS use of the material, seeded from `Material.coefficient`.
   * On AUTO rows it is the only markup — `coefficient` above is pinned to 1,
   * because the number belongs to the board and not to the row.
   */
  materialCoefficient: positiveDecimal.optional(),
  unitPriceNet: nonNegativeDecimal.default('0.00'),
  currency: countertopCurrencyEnum.default('RON'),
  notes: optionalTrim(2000),
  doubled: z.boolean().default(false),
  pricingMode: z.enum(['MANUAL', 'AUTO']).optional(),
  materialId: z.string().cuid().optional().nullable(),
  edgeId: z.string().cuid().optional().nullable(),
});
export type CreateCountertopRequest = z.infer<typeof createCountertopRequest>;

// ----- Create from a CatalogItem (snapshot) -----
//
// Catalog-specific attributes provide rich countertop fields. Dimension
// overrides snapshot the length/width shown in the countertop UI.
export const createCountertopFromCatalogRequest = z.object({
  catalogItemId: z.string().min(1),
  roomId: optionalCuid(),
  areaId: optionalCuid(),
  unitPriceNet: nonNegativeDecimal.optional(),
  currency: countertopCurrencyEnum.optional(),
  quantity: positiveIntegerDecimal.optional(),
  coefficient: positiveDecimal.optional(),
  materialCoefficient: positiveDecimal.optional(),
  notes: optionalTrim(2000),
  overrides: z
    .object({
      name: z.string().trim().min(1).max(200).optional(),
      sku: optionalTrim(50),
      widthMm: optionalDimMm,
      depthMm: optionalDimMm,
      thicknessMm: optionalDimMm,
      material: optionalTrim(100),
      edgeProfile: optionalTrim(100),
      doubled: z.boolean().optional(),
      pricingMode: z.enum(['MANUAL', 'AUTO']).optional(),
      materialId: z.string().cuid().optional().nullable(),
      edgeId: z.string().cuid().optional().nullable(),
    })
    .optional(),
});
export type CreateCountertopFromCatalogRequest = z.infer<typeof createCountertopFromCatalogRequest>;

// ----- Update (partial) -----
//
// `catalogItemId` is intentionally not patchable — the snapshot link is
// permanent. `projectId` is taken from the URL.
export const updateCountertopRequest = z
  .object({
    roomId: z.string().cuid().nullable(),
    areaId: z.string().cuid().nullable(),
    name: z.string().trim().min(1).max(200),
    sku: optionalTrim(50),
    widthMm: dimMm,
    depthMm: dimMm,
    thicknessMm: dimMm,
    material: optionalTrim(100),
    edgeProfile: optionalTrim(100),
    doubled: z.boolean(),
    quantity: positiveIntegerDecimal,
    coefficient: positiveDecimal,
    materialCoefficient: positiveDecimal,
    unitPriceNet: nonNegativeDecimal,
    currency: countertopCurrencyEnum,
    orderIndex: z.number().int().min(0),
    notes: optionalTrim(2000),
    pricingMode: z.enum(['MANUAL', 'AUTO']),
    materialId: z.string().cuid().nullable(),
    edgeId: z.string().cuid().nullable(),
  })
  .partial();
export type UpdateCountertopRequest = z.infer<typeof updateCountertopRequest>;
